import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { UserPreferencesService, PinnedScanner } from '../../@services/user-preferences.service';

// Renders the iframe for one of the user's pinned scanners. Route:
// `/admin/pinned/:key`. Pulls the pin from UserPreferencesService's
// cached signal, sanitizes the URL for `[src]`, renders full-height.
//
// If the target uses `X-Frame-Options: DENY` (as f2-tracker may today)
// the iframe will render blank — that's a follow-up on the target side,
// not this component. We surface a "won't load in a frame" hint after a
// timeout so the operator knows to click "open in new tab" instead.
@Component({
  selector: 'app-pinned-iframe',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './pinned-iframe.component.html',
  host: { class: 'block h-full' },
})
export class PinnedIframeComponent implements OnInit, OnDestroy {
  pin = signal<PinnedScanner | null>(null);
  safeUrl = signal<SafeResourceUrl | null>(null);
  loaded = signal(false);
  frameError = signal(false);

  // Timeout tracker for the "won't load in a frame" heuristic. If the
  // iframe hasn't fired `load` after N seconds, assume the target
  // refused (X-Frame-Options / CSP frame-ancestors) and show the hint.
  private _timeout: any = null;
  private _paramSub: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private prefs: UserPreferencesService,
    private sanitizer: DomSanitizer,
  ) {}

  async ngOnInit() {
    await this.prefs.loadOnce();
    this._paramSub = this.route.paramMap.subscribe((params) => {
      this._resolvePin(params.get('key') || '');
    });
  }

  ngOnDestroy() {
    this._paramSub?.unsubscribe();
    if (this._timeout) clearTimeout(this._timeout);
  }

  onIframeLoad() {
    this.loaded.set(true);
    if (this._timeout) { clearTimeout(this._timeout); this._timeout = null; }
  }

  /**
   * Open the pin in a new tab, routed through the SSO-redirect endpoint
   * so the browser lands on the target scanner WITH a fresh sid without
   * the SPA having to fetch-then-window.open (which was breaking on
   * async-return typing in the Vercel build).
   */
  openInNewTab() {
    const p = this.pin();
    if (!p?.url) return;
    window.open(this.prefs.ssoRedirectUrl(p.url), '_blank', 'noopener');
  }

  private _resolvePin(key: string) {
    const p = this.prefs.findPin(key);
    this.pin.set(p);
    this.loaded.set(false);
    this.frameError.set(false);
    if (this._timeout) { clearTimeout(this._timeout); this._timeout = null; }
    if (p?.url) {
      // Point the iframe at the same-origin sso-redirect endpoint —
      // backend mints a fresh sid on the operator's OWN tokens (from
      // the f2_id cookie which the browser sends since we're same-
      // origin) and 302-redirects to <target>?sid=<value>. Tracker's
      // middleware then bootstraps its session on that sid.
      const redirect = this.prefs.ssoRedirectUrl(p.url);
      this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(redirect));
      // 6s is generous — most well-behaved targets fire `load` within a
      // second. If we're still not loaded, hint that the target likely
      // refused framing.
      this._timeout = setTimeout(() => {
        if (!this.loaded()) this.frameError.set(true);
      }, 6000);
    } else {
      this.safeUrl.set(null);
    }
  }
}
